import { useMemo } from "react";
import { Blocks } from "./Blocks";
import { PresetShell } from "./PresetShell";
import {
  compactBlocks,
  createAtom,
  readObjects,
  readStatus,
  readString,
  readStringArray,
} from "./data";
import type { PresetViewProps } from "./CourseOverview";

/**
 * Plan de estudio: la respuesta del aula invertida.
 *
 * Cada paso se lee igual que en el curso —antes de clase, en clase, después—
 * y lleva su punto de control, que es lo que le dice al estudiante si puede
 * pasar al siguiente. El primer paso va marcado como el actual: un plan sin
 * "por dónde empiezo" no es un plan.
 */
export function StudyPath({ preset, data, emit, interactive }: PresetViewProps) {
  const goal = readString(data, "goal");
  const horizon = readString(data, "horizon");
  const start = readString(data, "start");
  const notes = readString(data, "notes");

  const steps = useMemo(
    () =>
      readObjects(data, "steps").map((step, index) => ({
        label: readString(step, "label") ?? `Paso ${index + 1}`,
        title: readString(step, "title") ?? `Paso ${index + 1}`,
        focus: readString(step, "focus"),
        actions: readStringArray(step, "actions"),
        checkpoint: readString(step, "checkpoint"),
        status: readStatus(step, "status") === "upcoming" && index === 0 ? "current" : readStatus(step, "status"),
      })),
    [data],
  );

  const blocks = compactBlocks([
    goal
      ? createAtom("callout", {
          variant: "key-idea",
          title: "Objetivo",
          body: goal,
        })
      : null,

    createAtom("metric_cards", {
      meta: { title: "El plan en cifras" },
      cards: [
        { label: "Plazo", value: horizon ?? "1 semana", hint: "Tiempo total del plan" },
        { label: "Pasos", value: String(steps.length), hint: "Antes, durante y después" },
        {
          label: "Puntos de control",
          value: String(steps.filter((step) => step.checkpoint !== null).length),
          hint: "Cómo saber que puedes avanzar",
        },
      ],
    }),

    steps.length > 0
      ? createAtom("timeline", {
          meta: { title: "Los pasos, en orden" },
          orientation: "vertical",
          items: steps.map((step) => ({
            label: step.label,
            title: step.title,
            description:
              step.actions.length > 0
                ? step.actions.map((action) => `• ${action}`).join("  ")
                : (step.checkpoint ?? null),
            duration: step.focus,
            tags: step.checkpoint ? ["Punto de control"] : [],
            status: step.status,
          })),
        })
      : null,

    start
      ? createAtom("callout", {
          variant: "tip",
          title: "Por dónde empezar",
          body: start,
        })
      : null,

    notes
      ? createAtom("prose", {
          meta: { title: "Notas" },
          markdown: notes,
        })
      : null,
  ]);

  return (
    <PresetShell
      eyebrow="Plan de estudio"
      title={preset.title ?? "Ruta de repaso"}
      lead={goal}
      chips={[
        ...(horizon ? [{ icon: "clock" as const, label: horizon }] : []),
        ...(steps.length > 0
          ? [{ icon: "list" as const, label: `${steps.length} pasos` }]
          : []),
      ]}
    >
      <Blocks blocks={blocks} emit={emit} interactive={interactive} />
    </PresetShell>
  );
}

export default StudyPath;
